import { transferClub } from '@/api/clubs/clubApi';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from '@/components/ui/use-toast';
import { cn } from '@/lib/utils';
import {
  transferClubSchema,
  type TransferClubFormData,
} from '@/schemas/transferClubSchema';
import { handleGlobalError } from '@/utils/error-handler';
import { zodResolver } from '@hookform/resolvers/zod';
import { AlertTriangle, ArrowLeftRight, Loader2, Mail } from 'lucide-react';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';

interface TransferClubDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  clubId: string;
  clubName: string;
  /** Called after a successful transfer so the parent can refresh its list. */
  onTransferred?: () => void;
}

type Step = 'email' | 'confirm';

export const TransferClubDialog: React.FC<TransferClubDialogProps> = ({
  open,
  onOpenChange,
  clubId,
  clubName,
  onTransferred,
}) => {
  const [step, setStep] = useState<Step>('email');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors, isValid },
    reset,
  } = useForm<TransferClubFormData>({
    resolver: zodResolver(transferClubSchema),
    mode: 'onChange',
    defaultValues: { email: '' },
  });

  const newOwnerEmail = watch('email');

  const closeAndReset = () => {
    onOpenChange(false);
    setStep('email');
    reset();
  };

  const goToConfirm = () => {
    setStep('confirm');
  };

  const doTransfer = async () => {
    try {
      setIsSubmitting(true);
      await transferClub(clubId, newOwnerEmail);

      toast({
        title: 'Club transferido',
        description: `"${clubName}" ahora es de ${newOwnerEmail}.`,
        variant: 'default',
      });

      closeAndReset();
      onTransferred?.();
    } catch (error) {
      handleGlobalError(
        error,
        'Error al transferir el club',
        'No se pudo transferir el club. Verificá que el email corresponda a un usuario registrado.',
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        // Radix only ever closes here — there is no trigger inside this dialog.
        if (!next && !isSubmitting) closeAndReset();
      }}
    >
      <DialogContent className="sm:max-w-[480px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ArrowLeftRight size={20} className="text-gc-danger" />
            Transferir club
          </DialogTitle>
          <DialogDescription className="text-left">
            Club:{' '}
            <span className="font-semibold text-gc-ink">{clubName}</span>
          </DialogDescription>
        </DialogHeader>

        {step === 'email' ? (
          <form onSubmit={handleSubmit(goToConfirm)} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="transfer-email" className="text-sm font-medium">
                Email del nuevo dueño
              </Label>
              <div className="relative">
                <Mail
                  size={18}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                />
                <Input
                  id="transfer-email"
                  type="email"
                  autoComplete="off"
                  placeholder="ejemplo@correo.com"
                  className={cn('pl-10', errors.email && 'border-red-500')}
                  {...register('email')}
                />
              </div>
              {errors.email && (
                <p className="text-sm text-red-500">{errors.email.message}</p>
              )}
              <p className="text-[13px] text-gc-muted">
                Tiene que ser un usuario ya registrado en GetMatch.
              </p>
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={closeAndReset}
              >
                Cancelar
              </Button>
              <Button type="submit" disabled={!isValid}>
                Continuar
              </Button>
            </DialogFooter>
          </form>
        ) : (
          <>
            <div className="flex items-start gap-3 rounded-lg border border-red-200 bg-red-50 p-4">
              <AlertTriangle
                size={20}
                className="mt-0.5 flex-shrink-0 text-gc-danger"
              />
              <div className="space-y-2 text-sm">
                <p className="text-gc-ink">
                  <span className="font-semibold">"{clubName}"</span> pasa a ser
                  de <span className="font-semibold">{newOwnerEmail}</span>.
                </p>
                <p className="text-red-800">
                  Vas a perder el acceso al club y no vas a poder revertirlo.
                </p>
              </div>
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setStep('email')}
                disabled={isSubmitting}
              >
                Volver
              </Button>
              <Button
                type="button"
                onClick={doTransfer}
                disabled={isSubmitting}
                className="bg-gc-danger hover:bg-gc-danger/90"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Transfiriendo...
                  </>
                ) : (
                  'Transferir club'
                )}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
